import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Activity, Mail, ArrowRight } from 'lucide-react';
import logo from '../assets/images/turret-dark.svg';
import { buildApiUrl } from '../config/api';

interface SignInProps {
  onError: (error: string) => void;
}

export default function SignIn({ onError }: SignInProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      onError('Please enter your email address');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(buildApiUrl('api/v1/auth/magic_link'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send magic link');
      }

      setMessage(data.message);
      setMagicLinkSent(true);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setMagicLinkSent(false);
    setMessage('');
    setEmail('');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <img src={logo} alt="Turret" className="h-8 w-8 text-primary" />
            {/* <Activity className="h-8 w-8 text-primary" /> */}
            <h1 className="text-2xl font-bold">Turret</h1>
          </div>
          <CardTitle className="text-xl">
            {magicLinkSent ? 'Check Your Email' : 'Sign In'}
          </CardTitle>
          <CardDescription>
            {magicLinkSent 
              ? 'We\'ve sent you a magic link to sign in' 
              : 'Enter your email to receive a magic link'
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!magicLinkSent ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  Email Address
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  disabled={loading}
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Send Magic Link
                  </>
                )}
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Mail className="h-8 w-8 text-green-600" />
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  {message}
                </p>
                <p className="text-sm text-muted-foreground">
                  Check your email for <strong>{email}</strong> and click the magic link to sign in.
                </p>
              </div>
              
              <div className="border-t pt-4">
                <Button 
                  variant="outline" 
                  onClick={handleReset}
                  className="w-full"
                >
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Try Different Email
                </Button>
              </div>
            </div>
          )}
          
          <div className="text-center text-xs text-muted-foreground mt-6">
            <p>
              Don't have an account? One will be created automatically when you sign in.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 